import React, { createContext, useContext, useRef, useEffect, useState } from "react";
import { initSocketConnection, subscribeToSocket } from "../utils/socket";
import { useDispatch, useSelector } from "react-redux";
import { getServiceCity } from "../redux/actions/UserServiceCity";
import SOSAlertPopup from "../components/sos/SOSAlertPopup";

const SocketContext = createContext({
  socket: null,
  isConnected: false,
  drivers: [], // Add drivers to context
});

export function SocketProvider({ children }) {
  const dispatch = useDispatch();
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [sosAlert, setSosAlert] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [drivers, setDrivers] = useState([]); // State for drivers data

  const locations = useSelector((state) => state?.userServiceCity?.userServiceCity?.data || []);
  const locationId = useSelector((state) => state?.locationDropdown?.selectedLocation?.id);

  // Fetch locations only on mount
  useEffect(() => {
    if (!locations || locations.length === 0) {
      dispatch(getServiceCity());
    }
  }, []);

  // Main socket effect
  useEffect(() => {
    const url = process.env.REACT_APP_API_BASE_URL;
    if (!url) return;

    // Initialize socket connection
    socketRef.current = initSocketConnection(url, {
      socketPath: "/livetrip-api/customer",
      locationId
    });

    // Connection status handlers
    const onConnect = () => {
      setIsConnected(true);
      setDrivers([]);
      console.log('Socket connected');
    };

    const onDisconnect = () => {
      setIsConnected(false);
      setDrivers([]);
      console.log('Socket disconnected');
    };

    socketRef.current.on('connect', onConnect);
    socketRef.current.on('disconnect', onDisconnect);

    // SOS Alert handler
    const unsubscribeSos = subscribeToSocket("sosAlert", (data) => {
      setSosAlert(data);
      setIsPopupOpen(true);
    });

    // Drivers location handlers
    const unsubscribeInitial = subscribeToSocket("InitialLiveLocationFeed", (data) => {
      setDrivers(Array.isArray(data?.drivers) ? data.drivers : []);
    });

    // Cleanup function
    return () => {
      unsubscribeSos();
      unsubscribeInitial();
      if (socketRef.current) {
        socketRef.current.off('connect', onConnect);
        socketRef.current.off('disconnect', onDisconnect);
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setIsConnected(false);
      setDrivers([]); // Clear drivers on disconnect
    };
  }, [locationId]);

  const handleTakeAction = () => {
    setIsPopupOpen(false);
    setSosAlert(null);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setSosAlert(null);
  };

  // Stable context value
  const contextValue = {
    socket: socketRef.current,
    isConnected,
    drivers, // Include drivers in context
  };

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
      <SOSAlertPopup
        sosAlert={sosAlert}
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
        onTakeAction={handleTakeAction}
      />
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}